import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Typography, TextField, Button, Alert, CircularProgress,
  Divider, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';

const API = process.env.REACT_APP_API_URL || '';

const VACIO = {
  nombre: '', apellido: '', dni: '', fecha_nacimiento: '',
  telefono: '', direccion: '', obra_social: '', numero_afiliado: '',
};

export default function MisDatos({ usuario, onConfirmado }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(VACIO);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [obrasSociales, setObrasSociales] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/pacientes/me`, {
      headers: { Authorization: `Bearer ${usuario.token}` }
    }).then(r => {
      const d = r.data;
      setForm({
        nombre:           d.nombre || '',
        apellido:         d.apellido || '',
        dni:              d.dni || '',
        fecha_nacimiento: d.fecha_nacimiento ? String(d.fecha_nacimiento).split('T')[0] : '',
        telefono:         d.telefono || '',
        direccion:        d.direccion || '',
        obra_social:      d.obra_social || '',
        numero_afiliado:  d.numero_afiliado || '',
      });
    }).catch(() => {}).finally(() => setCargando(false));
    axios.get(`${API}/api/obras-sociales`).then(r => setObrasSociales(r.data)).catch(() => {});
  }, [usuario.token]);

  async function guardar() {
    const requeridos = [
      { key: 'nombre', label: 'Nombre' },
      { key: 'apellido', label: 'Apellido' },
      { key: 'dni', label: 'DNI' },
      { key: 'fecha_nacimiento', label: 'Fecha de nacimiento' },
      { key: 'telefono', label: 'Teléfono' },
    ];
    const faltantes = requeridos.filter(c => !form[c.key]?.trim());
    if (faltantes.length) {
      setError(`Completá los campos obligatorios: ${faltantes.map(c => c.label).join(', ')}`);
      return;
    }
    setGuardando(true);
    setError('');
    try {
      await axios.put(`${API}/api/pacientes/me`, form, {
        headers: { Authorization: `Bearer ${usuario.token}` }
      });
      onConfirmado();
      navigate('/panel-paciente');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar los datos.');
    } finally {
      setGuardando(false);
    }
  }

  const col = { color: 'var(--c-acento)' };
  const colDis = { color: '#78909c' };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      color: '#e0e0e0',
      '& fieldset': { borderColor: 'var(--c-primario)' },
      '&:hover fieldset': { borderColor: 'var(--c-acento)' },
      '&.Mui-focused fieldset': { borderColor: 'var(--c-acento)' },
    },
    '& .MuiInputLabel-root': { color: 'var(--c-acento)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'var(--c-acento)' },
  };

  const fieldSxDis = {
    '& .MuiOutlinedInput-root': {
      color: '#78909c',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
    },
    '& .MuiInputLabel-root': { color: '#78909c' },
  };

  const dateSx = {
    ...fieldSx,
    '& input::-webkit-calendar-picker-indicator': { filter: 'invert(0.6)' },
  };

  const dniConfirmado = usuario.datos_confirmados;

  if (cargando) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress sx={{ color: 'var(--c-acento)' }} />
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <PersonIcon sx={{ color: 'var(--c-acento)', fontSize: 32 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)' }}>
          {usuario.datos_confirmados ? 'Mis datos' : 'Completá tus datos'}
        </Typography>
      </Box>
      {!usuario.datos_confirmados && (
        <Typography sx={{ color: '#b0b0b0', mb: 3, fontSize: 14 }}>
          Para continuar necesitamos que confirmes tu información personal.
        </Typography>
      )}

      <Box sx={{ bgcolor: 'var(--c-superficie)', borderRadius: 2, p: 3, border: '1px solid var(--c-primario)' }}>
        <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>

          {/* Nombre */}
          <TextField label="Nombre *" value={form.nombre}
            onChange={e => setForm({ ...form, nombre: e.target.value })}
            InputLabelProps={{ style: col, shrink: true }}
            inputProps={{ style: { color: '#e0e0e0' } }}
            sx={fieldSx} />

          {/* Apellido */}
          <TextField label="Apellido *" value={form.apellido}
            onChange={e => setForm({ ...form, apellido: e.target.value })}
            InputLabelProps={{ style: col, shrink: true }}
            inputProps={{ style: { color: '#e0e0e0' } }}
            sx={fieldSx} />

          {/* DNI */}
          <TextField
            label={dniConfirmado ? 'DNI (no modificable)' : 'DNI *'}
            value={form.dni}
            onChange={e => !dniConfirmado && setForm({ ...form, dni: e.target.value })}
            disabled={dniConfirmado}
            InputLabelProps={{ style: dniConfirmado ? colDis : col, shrink: true }}
            inputProps={{ style: { color: dniConfirmado ? '#78909c' : '#e0e0e0' } }}
            sx={dniConfirmado ? fieldSxDis : fieldSx} />

          {/* Fecha de nacimiento */}
          <Box style={{ position: 'relative' }}>
            <Box component="span" style={{
              position: 'absolute', top: -10, left: 10, zIndex: 1,
              background: 'var(--c-superficie)', padding: '0 4px',
              color: 'var(--c-acento)', fontSize: 12, pointerEvents: 'none'
            }}>
              Fecha de nacimiento *
            </Box>
            <input
              type="date"
              value={form.fecha_nacimiento}
              onChange={e => setForm({ ...form, fecha_nacimiento: e.target.value })}
              style={{
                width: '100%', boxSizing: 'border-box',
                height: 56, padding: '0 14px',
                background: 'transparent', color: '#e0e0e0',
                border: '1px solid var(--c-primario)', borderRadius: 4,
                fontSize: 16, colorScheme: 'dark', outline: 'none',
                fontFamily: 'inherit', cursor: 'pointer'
              }}
              onFocus={e => e.target.style.borderColor = 'var(--c-acento)'}
              onBlur={e => e.target.style.borderColor = 'var(--c-primario)'}
            />
          </Box>

          {/* Teléfono */}
          <TextField label="Teléfono *" value={form.telefono}
            onChange={e => setForm({ ...form, telefono: e.target.value })}
            InputLabelProps={{ style: col, shrink: true }}
            inputProps={{ style: { color: '#e0e0e0' } }}
            sx={fieldSx} />

          {/* Dirección */}
          <TextField label="Dirección" value={form.direccion}
            onChange={e => setForm({ ...form, direccion: e.target.value })}
            InputLabelProps={{ style: col, shrink: true }}
            inputProps={{ style: { color: '#e0e0e0' } }}
            sx={fieldSx} />

          {/* Obra social */}
          <FormControl sx={fieldSx}>
            <InputLabel sx={{ color: 'var(--c-acento)' }}>Obra social</InputLabel>
            <Select
              value={form.obra_social}
              label="Obra social"
              onChange={e => setForm({ ...form, obra_social: e.target.value })}
              sx={{ color: '#e0e0e0', '& .MuiSvgIcon-root': { color: 'var(--c-acento)' } }}
              MenuProps={{ PaperProps: { sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } } }}
            >
              <MenuItem value=""><em style={{ color: '#888' }}>Sin especificar</em></MenuItem>
              {obrasSociales.map(os => (
                <MenuItem key={os.id} value={os.nombre}>{os.nombre}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* N° afiliado */}
          <TextField label="N° de afiliado" value={form.numero_afiliado}
            onChange={e => setForm({ ...form, numero_afiliado: e.target.value })}
            InputLabelProps={{ style: col, shrink: true }}
            inputProps={{ style: { color: '#e0e0e0' } }}
            sx={fieldSx} />

        </Box>

        <Divider sx={{ borderColor: 'var(--c-primario)', my: 2 }} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Button
          variant="contained"
          onClick={guardar}
          disabled={guardando}
          fullWidth
          sx={{ bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' }, py: 1.2 }}
        >
          {guardando ? 'Guardando...' : 'Confirmar datos'}
        </Button>
      </Box>
    </Box>
  );
}
